import React, { useContext, useEffect } from "react";
import {
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Typography,
  Link
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/styles";
import {
  PowerSettingsNew,
  CheckBoxOutlined,
  FileCopyOutlined,
  AppsOutlined,
  AccountBox,
  ViewListOutlined,
  FormatListNumberedOutlined,
  Description
} from "@material-ui/icons";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MenuIcon from "@material-ui/icons/Menu";
import clsx from "clsx";
import useDialog from "../../../hooks/dialog/useDialog";
import UserContext from "../../../UserContext";
import LinkAdapter from "./LinkAdapter";
import UserRole from "../../../models/user/UserRole";
import {
  financingRequestsUrl,
  paymentRequestsUrl,
  adminAnnualAdministrationsUrl,
  adminFinancingRequestsUrl,
  adminPaymentRequestsUrl,
  adminPaymentsUrl,
  manualsUrl
} from "../../../urls";
import TrDialog from "../controls/TrDialog";

const drawerWidth = 350;

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    overflowX: "hidden",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9)
    }
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "70px 18px 0px 8px",
    ...theme.mixins.toolbar
  },
  divider: {
    backgroundColor: "#ffffff"
  },
  listItemIcon: {
    color: "#ffffff"
  },
  listItemText: {
    fontWeight: "bold",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  bot: {
    position: "absolute",
    bottom: 0,
    width: "100%"
  },
  helpText: {
    paddingTop: theme.spacing(3)
  },
  helpLink: {
    width: "100%",
    color: "#e0e0e0"
  },
  linkText: {
    color: "#e0e0e0"
  }
}));

const paymentRequestDisabled =
  process.env.REACT_APP_MONTHLY_PAYMENT_REQUEST_DISABLED;

export default function SideNav() {
  const classes = useStyles({});
  const { user, logoutUser } = useContext(UserContext);

  const [open, setOpen] = React.useState(true);

  const theme: Theme = useTheme();
  const isSmallSizeWidth = useMediaQuery(theme.breakpoints.down("sm"));

  const [logoutDialogProps, openLogoutDialog] = useDialog({
    isOpened: false,
    title: "Odjava",
    message: "Jeste li sigurni da se želite odjaviti iz sustava?",
    cancelButtonText: "Odustani",
    okButtonText: "Odjava",
    onOkButtonClick: () => {
      sessionStorage.clear();
      logoutUser();
    }
  });

  const trunc = (
    content: { length: number; substr: (arg0: number, arg1: number) => string },
    length: number
  ) => {
    return content.length > length
      ? content.substr(0, length - 1) + "..."
      : content;
  };

  useEffect(() => {
    if (isSmallSizeWidth) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isSmallSizeWidth]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const generateMenu = () => {
    let menu = null;
    if (user.roleId === UserRole.Ministry) {
      menu = (
        <>
          <ListItem
            button
            component={LinkAdapter}
            to={adminAnnualAdministrationsUrl()}
          >
            <ListItemIcon>
              <AppsOutlined classes={{ root: classes.listItemIcon }} />
            </ListItemIcon>
            <ListItemText primary="Administracija" />
          </ListItem>
          <ListItem
            button
            component={LinkAdapter}
            to={adminFinancingRequestsUrl()}
          >
            <ListItemIcon>
              <CheckBoxOutlined classes={{ root: classes.listItemIcon }} />
            </ListItemIcon>
            <ListItemText primary="Lista godišnjih zahtjeva" />
          </ListItem>
          <ListItem
            button
            component={LinkAdapter}
            to={adminPaymentRequestsUrl()}
          >
            <ListItemIcon>
              <ViewListOutlined classes={{ root: classes.listItemIcon }} />
            </ListItemIcon>
            <ListItemText primary="Lista mjesečnih zahtjeva" />
          </ListItem>
          <ListItem button component={LinkAdapter} to={adminPaymentsUrl()}>
            <ListItemIcon>
              <FileCopyOutlined classes={{ root: classes.listItemIcon }} />
            </ListItemIcon>
            <ListItemText primary="Generiranje liste za isplatu" />
          </ListItem>
        </>
      );
    } else {
      menu = (
        <>
          <ListItem button component={LinkAdapter} to={financingRequestsUrl()}>
            <ListItemIcon>
              <FormatListNumberedOutlined
                classes={{ root: classes.listItemIcon }}
              />
            </ListItemIcon>
            <ListItemText primary="Lista zahtjeva za sufinanciranjem" />
          </ListItem>
          <ListItem
            button
            component={LinkAdapter}
            to={paymentRequestsUrl()}
            disabled={paymentRequestDisabled == "true"}
          >
            <ListItemIcon>
              <ViewListOutlined classes={{ root: classes.listItemIcon }} />
            </ListItemIcon>
            <ListItemText primary="Lista zahtjeva za isplatom" />
          </ListItem>
        </>
      );
    }

    return menu;
  };

  return (
    <Drawer
      variant="permanent"
      classes={{
        paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose)
      }}
      open={open}
    >
      <div className={classes.toolbarIcon}>
        <IconButton
          onClick={open ? handleDrawerClose : handleDrawerOpen}
          color="inherit"
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </div>
      <List>
        <Divider classes={{ root: classes.divider }} />
        <ListItem>
          <ListItemIcon>
            <AccountBox classes={{ root: classes.listItemIcon }} />
          </ListItemIcon>
          <ListItemText
            title={
              user.institutionName +
              (user.roleId === UserRole.Ministry
                ? ""
                : " (" + user.institutionCode + ")")
            }
            primary={trunc(
              user.institutionName +
              (user.roleId === UserRole.Ministry
                ? ""
                : " (" + user.institutionCode + ")"),
              30
            )}
            classes={{ primary: classes.listItemText }}
          />
        </ListItem>
        <Divider classes={{ root: classes.divider }} />
        {generateMenu()}
        <ListItem button onClick={openLogoutDialog}>
          <ListItemIcon>
            <PowerSettingsNew classes={{ root: classes.listItemIcon }} />
          </ListItemIcon>
          <ListItemText primary="Odjava" />
        </ListItem>
      </List>
      <List className={classes.bot}>
        <Divider />
        <ListItem
          button
          component={LinkAdapter}
          to={manualsUrl()}
          target="_blank"
        >
          <ListItemIcon>
            <Description classes={{ root: classes.listItemIcon }} />
          </ListItemIcon>
          <ListItemText primary="Korisničke upute" />
        </ListItem>

        <ListItem className={classes.helpText}>
          <ListItemIcon>
            <></>
          </ListItemIcon>
          <ListItemText>
            {/* Should use <HelpEmailLink /> but here we have problem with how navigation works so this is a work around.*/}
            <Typography variant="caption" className={classes.helpLink}>
              Sve probleme u vezi korištenja aplikacije
              <br /> možete prijaviti na e-mail adresu
              <br />
              <Link
                href="mailto:etur@mzom.hr"
                target="_blank"
                className={classes.linkText}
              >
                etur@mzom.hr
              </Link>
              .
            </Typography>
          </ListItemText>
        </ListItem>
      </List>
      <TrDialog {...logoutDialogProps} />
    </Drawer>
  );
}
